/// <amd-module name="Repos/PullRequest/Attachments/Service/PullRequestAttachmentService" />
import { Attachment } from "Repos/Common/Contracts/Git";
import { IPullRequestMarkdownToolbarContext } from "Repos/Common/Contracts/Markdown";
import * as Resources from "Repos/PullRequest/Attachments/Resources";
import { format } from "VSS/Core/Util/String";
import { insertText } from "VSS/Features/MarkdownEditor/Formatting/Common";
import { IMarkdownFormattingArgs, IMarkdownFormattingResult } from "VSS/Features/MarkdownEditor/Formatting/Contracts";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IMenuItem } from "VSSUI/Menu";
import { PullRequestAttachmentTelemetryConstants  as TelemetryConstants } from "../TelemetryConstants";
import { PullRequestAttachmentCommand, validateFile } from "../PullRequestAttachmentCommand";
import { getGitClient } from "../RestClient/Git";

export interface IPullRequestAttachmentService extends IVssService {
    /**
     * Uploads the files to the pull request in the given pull request context
     * and adds the file links (or images if the file type is an image) to the specified markdown
     * at the specified location
     *
     * @param pullRequestContext Context about the current pull request like id, repository id and project id
     * @param files Files to upload and add to the markdown
     * @param formattingArgs Details about the current markdown to add attachment links like text and current selection
     */
    addAttachmentsToMarkdown(
        pullRequestContext: IPullRequestMarkdownToolbarContext,
        files: File[],
        formattingArgs: IMarkdownFormattingArgs,
        errorHandler: (error: Error) => void
    ): Promise<IMarkdownFormattingResult>;

    /**
     * Approximates the length of the content if the temp urls were replaced by the actual urls.
     *
     * @param content Content whose true length will be calculated
     * @param hostName The host name
     */
    calculateTrueContentLength(content: string, hostName: string): number;

    /**
     * Replaces temp and old blobs attachment urls in the given content with the actual ones
     * uploaded to the server.
     *
     * @param pullRequestContext Context about the current pull request like id, repository id and project id
     * @param content Content to replace attachments
     */
    replaceAttachmentUrls(pullRequestContext: IPullRequestMarkdownToolbarContext, content: string): Promise<string>;

    /**
     * Uploads the files that are temporarily attached to the pull request and then replaces the Urls in the given
     * content with the actual ones uploaded to the server.
     *
     * @param pullRequestContext Context about the current pull request like id, repository id and project id
     * @param content Content to replace attachments
     */
    tryUpdateBlobs(pullRequestContext: IPullRequestMarkdownToolbarContext, content: string): Promise<string>;
}

function stripBadCharacters(filename: string): string {
    // based on https://mseng.visualstudio.com/VSOnline/_git/VSO?path=%2FTfs%2FService%2FAdmin%2FWeb%2Fweb.config.template&version=GBmaster&_a=contents&line=191&lineStyle=plain&lineEnd=191&lineStartColumn=48&lineEndColumn=67
    // but also I added $, [, ] because those were messing with markdown
    // Also replace + since IIS doesn't like pluses in the URL path, even when encoded as %2B.
    return filename.replace(/[\<\>\*\%\&\:\\\?\$\[\]\+]/g, ".");
}

interface LocalAttachment extends Attachment {
    tempUrl: string;
}

class PullRequestAttachmentService extends VssService implements IPullRequestAttachmentService, IMenuService {
    private localAttachmentNames: string[] = [];
    private serverAttachments: Promise<Attachment[] | void> | undefined = undefined;
    private tempAttachmentUrls: string[] = [];
    private tempAttachmentsMap: { [tempUrl: string]: Promise<LocalAttachment | void> } = {};
    private prCreationTempFilesMap: { [tempUrl: string]: { file: File; name: string } } = {};
    // Finds images in markdown that has blob link. The first group is file's name and the second one is link.
    private findBlobRegexp = /!\[(.*)\]\((blob:.*)\)/;
    private empty = "";

    public async addAttachmentsToMarkdown(
        pullRequestContext: IPullRequestMarkdownToolbarContext,
        files: File[],
        formattingArgs: IMarkdownFormattingArgs,
        errorHandler: (error: Error) => void
    ): Promise<IMarkdownFormattingResult> {
        // Validate files before uploading
        files.forEach(file => validateFile(file));

        const gitClient = getGitClient(this.pageContext);

        // We get all the attachments from the server and combine them with the newly
        // added ones to have uniqe file names like image.png, image (2).png, image (3).png, etc.
        const attachmentNames = [...this.localAttachmentNames];

        pullRequestContext.pullRequestId !== 0 &&
            attachmentNames.unshift(...((await this.getServerAttachments(pullRequestContext, errorHandler)) || []).map(a => a.displayName));

        // We don't wait for the files get uploaded. Instead, adding the file
        // from the blob. Once the markdown content is persisted, temp url are
        // replaced with actual file urls
        let markdownText = "";
        for (const file of files) {
            const filename = this.getUniqueFilename(attachmentNames, file.name);
            this.localAttachmentNames.push(filename);

            // Use a blob object to display preview immediately in the markdown preview
            const isImage = file.type.indexOf("image/") === 0;
            const blob = new Blob([file], { type: file.type });
            const blobUrl = URL.createObjectURL(blob);
            markdownText += (isImage ? "![" : "[") + filename + "](" + blobUrl + ") ";

            if (pullRequestContext.pullRequestId !== 0) {
                // We don't wait for upload to complete. Just add the markdown
                // text with temp urls. Actual file url will be replaced later
                const newAttachment = gitClient.createAttachment(
                    file,
                    filename,
                    pullRequestContext.repositoryId,
                    pullRequestContext.pullRequestId,
                    pullRequestContext.projectId
                );

                this.tempAttachmentsMap[blobUrl] = newAttachment.then(
                    a => {
                        return { ...a, tempUrl: blobUrl };
                    },
                    error => {
                        const formattedError = new Error(format(Resources.ErrorUploadingAttachment, filename, error.message));
                        errorHandler(formattedError);
                    }
                );
            } else {
                this.prCreationTempFilesMap[blobUrl] = { file, name: filename };
            }

            this.tempAttachmentUrls.push(blobUrl);
        }

        return insertText(formattingArgs, markdownText);
    }

    public calculateTrueContentLength(content: string, hostName: string): number {
        content = content || "";
        let length = content.length;
        for (const tempUrl of Object.keys(this.prCreationTempFilesMap)) {
            const count = content.split(tempUrl).length - 1;
            // Remove the length of the old URL + "blob:"
            length -= (tempUrl.length + 5) * count;

            // The length of the new URL will be the origin + the hostname + the file name + 132.
            // Slashes around the hostname (+2)
            // The string "/_apis/git/repositories/" is added (+24)
            // 2 GUIDs (+72)
            // The string "/pullRequests/" is added (+14)
            // The pull request id is added (Assume +7)
            // The string "/attachments/" is added (+13)
            let newURLLength = location.origin.length + hostName.length + 132 + this.prCreationTempFilesMap[tempUrl].name.length;
            length += count * newURLLength;
        }

        return length;
    }

    public getMenuItems(menuId: string, contextualData: IPullRequestMarkdownToolbarContext): IMenuItem[] {
        return [
            new PullRequestAttachmentCommand({
                addAttachmentToMarkdown: (pullRequestContext, files, formattingArgs, errorHandler) =>
                    this.addAttachmentsToMarkdown(pullRequestContext, files, formattingArgs, errorHandler),
                pageContext: this.pageContext,
                pullRequestContext: contextualData
            })
        ];
    }

    public async tryUpdateBlobs(pullRequestContext: IPullRequestMarkdownToolbarContext, content: string): Promise<string> {
        const gitClient = getGitClient(this.pageContext);
        content = content || "";

        for (const tempUrl of Object.keys(this.prCreationTempFilesMap)) {
            const newAttachment = gitClient.createAttachment(
                this.prCreationTempFilesMap[tempUrl].file,
                this.prCreationTempFilesMap[tempUrl].name,
                pullRequestContext.repositoryId,
                pullRequestContext.pullRequestId,
                pullRequestContext.projectId
            );

            this.tempAttachmentsMap[tempUrl] = newAttachment.then(
                a => {
                    return { ...a, tempUrl: tempUrl };
                },
                error => {
                    const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                    telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.RepoPRAttachmentCreationFailed, { error });
                    throw new Error(format(Resources.ErrorUploadingAttachment, this.prCreationTempFilesMap[tempUrl].file, error.message));
                }
            );
        }

        return this.replaceTempAttachmentUrls(content);
    }

    public async replaceAttachmentUrls(pullRequestContext: IPullRequestMarkdownToolbarContext, content: string): Promise<string> {
        await this.updateOldBlobsUrl(pullRequestContext, content);
        return this.replaceTempAttachmentUrls(content);
    }

    private async replaceTempAttachmentUrls(content: string): Promise<string> {
        content = content || "";
        if (content) {
            const uploadPromises = this.tempAttachmentUrls.map(url => this.tempAttachmentsMap[url]);
            const attachments = await Promise.all(uploadPromises);
            for (const attachment of attachments) {
                if (attachment) {
                    let tempUrlIndex: number;
                    while ((tempUrlIndex = content.indexOf(attachment.tempUrl)) >= 0) {
                        // Replace temp URL with actual attachment URL
                        content = content.substring(0, tempUrlIndex) + attachment.url + content.substring(tempUrlIndex + attachment.tempUrl.length);
                    }
                }
            }
            return content;
        }

        return Promise.resolve(content);
    }

    /**
     * Updates temporarily attached files that failed to be updated before with the actual urls.
     *
     * @param pullRequestContext Context about the current pull request like id, repository id and project id
     * @param content Content to replace attachments
     */
    private async updateOldBlobsUrl(pullRequestContext: IPullRequestMarkdownToolbarContext, content: string): Promise<void> {
        if (!isFeatureFlagEnabled(this.pageContext, "SourceControl.GitPullRequests.UpdateOldBlobsUrl", false) || !content) return;
        const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        let blobMatch = content.match(this.findBlobRegexp);
        if (!blobMatch) return;
        let allAttachments = await this.getAllAttachments(pullRequestContext);
        while (blobMatch) {
            this.putImageToTempAttachments(allAttachments, blobMatch[1], blobMatch[2], telemetryService)
            content = content.replace(blobMatch[0], this.empty);
            blobMatch = content.match(this.findBlobRegexp);
        }
    }

    private getServerAttachments(
        pullRequestContext: IPullRequestMarkdownToolbarContext,
        errorHandler: (error: Error) => void
    ): Promise<Attachment[] | void> {
        if (!this.serverAttachments) {
            this.serverAttachments = getGitClient(this.pageContext)
                .getAttachments(pullRequestContext.repositoryId, pullRequestContext.pullRequestId, pullRequestContext.projectId)
                .then(
                    attachments => attachments,
                    error => {
                        // Format error to give more context about the error
                        const formattedError = new Error(format(Resources.ErrorLoadingAttachments, error.message));
                        errorHandler(formattedError);

                        // Reset server attachments so that retry will hit the server again
                        this.serverAttachments = undefined;
                    }
                );
        }

        return this.serverAttachments;
    }

    private getUniqueFilename(attachmentNames: string[], filename: string): string {
        filename = stripBadCharacters(filename);

        const attachmentsMap: { [name: string]: boolean } = {};
        attachmentNames.forEach(name => (attachmentsMap[name.toLocaleUpperCase()] = true));

        let conflict = attachmentsMap[filename.toLocaleUpperCase()];
        if (!conflict) {
            return filename;
        }

        let baseName = filename;
        let ext = "";
        const lastDot = filename.lastIndexOf(".");
        if (lastDot >= 0) {
            baseName = filename.substring(0, lastDot);
            ext = filename.substring(lastDot);
        }

        let count = 2;
        let uniqueName = "";
        while (conflict) {
            uniqueName = baseName + " (" + count + ")" + ext;
            conflict = attachmentsMap[uniqueName.toLocaleUpperCase()];
            ++count;
        }

        return uniqueName;
    }

    private async getAllAttachments(pullRequestContext: IPullRequestMarkdownToolbarContext): Promise<Attachment[]> {
        return getGitClient(this.pageContext).getAttachments(
            pullRequestContext.repositoryId,
            pullRequestContext.pullRequestId,
            pullRequestContext.projectId
        );
    }

    private putImageToTempAttachments(allAttachments: Attachment[], imageName: string, tempUrl: string, telemetryService: IVssTelemetryService) {
        if (this.tempAttachmentsMap[tempUrl] != undefined) return
        const attachment = allAttachments.find(it => it.displayName == imageName);
        if (!attachment) return;
        telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.RepoPROldImageUpdated, { count: 1 });
        this.tempAttachmentsMap[tempUrl] = Promise.resolve({...attachment, tempUrl: tempUrl})
        this.tempAttachmentUrls.push(tempUrl);
    }
}

Services.add("pull-request-attachment-service", { serviceFactory: PullRequestAttachmentService });
